import { Trash2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import {
  DIALOG_VIEWPORT_ANCHOR_CLASS,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { Separator } from '@/components/atoms/separator';
import { cn } from '@/libs/utils/cn';

interface ConfirmDeleteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => Promise<void> | void;
  isDeleting?: boolean;
  /** Short heading shown next to the trash icon, e.g. "Delete project". */
  title: string;
  /** Prominent confirmation line, e.g. "Confirm you want to delete this project". */
  heading: string;
  /** Supporting line explaining the consequence of the deletion. */
  description: string;
  /**
   * Container to portal into (e.g. `shell.containerRef.current` on the map)
   * so the modal centers there instead of the whole screen. Omit for the
   * default full-screen centering.
   */
  dialogContainer?: HTMLElement;
  /** Extra overlay classes, e.g. a higher z-index so the scrim covers open menus. */
  overlayClassName?: string;
  /** Extra panel classes, paired with `overlayClassName` so the dialog stays above its scrim. */
  contentClassName?: string;
}

/**
 * Shared confirmation dialog for destructive delete/remove actions. Feature
 * components supply the copy (`title`, `heading`, `description`) and the
 * `onConfirm` handler; this component owns the layout and the Cancel/Delete
 * buttons.
 */
export function ConfirmDeleteModal({
  open,
  onOpenChange,
  onConfirm,
  isDeleting,
  title,
  heading,
  description,
  dialogContainer,
  overlayClassName,
  contentClassName,
}: ConfirmDeleteModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'flex max-w-lg flex-col gap-6 overflow-y-auto max-h-[min(90dvh,calc(100dvh-3.5rem-2rem))]',
          dialogContainer ? 'absolute' : DIALOG_VIEWPORT_ANCHOR_CLASS,
          contentClassName,
        )}
        overlayClassName={cn(dialogContainer && 'absolute inset-0', overlayClassName)}
        dialogContainer={dialogContainer}
      >
        <DialogHeader className="gap-1">
          <DialogTitle className="text-primary-700 text-lg font-normal inline-flex items-center gap-2">
            <Trash2 className="text-destructive" size={18} /> {title}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <div className="text-primary text-xl font-light">{heading}</div>
            <div className="font-normal text-muted-foreground">{description}</div>
          </div>
          <Separator />
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" size="lg">
              Cancel
            </Button>
          </DialogClose>
          <Button
            className="text-destructive"
            type="button"
            variant="outline"
            size="lg"
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? (
              'Deleting...'
            ) : (
              <>
                <Trash2 /> Delete
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
